import * as React from "react";
import { Helmet } from "react-helmet";

import AppLayout from "../../components/AppLayout/AppLayout";
import styled from "../../styled-components";
import { media } from "../../breakpoints";
import Footer from "../../components/Footer/Footer";
import { Link } from "react-router-dom";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background-color: #ffffff;
`;

const MainContent = styled.section`
  color: ${props => props.theme.colors.black};
  background-color: #ffffff;
  padding: 0 20px 90px;
  margin: 100px 0 0 0;

  ${media.TABLET`
    padding-bottom: 100px;
    padding-top: 50px;
  `};

  h1 {
    color: ${props => props.theme.colors.black};
    font-family: ${props => props.theme.fonts.sans};
    font-size: 36px;
    font-weight: 400;
    margin: 25px 0;
    text-align: center;
    text-shadow: 0 0 4px rgba(0, 0, 0, 0.12);

    ${media.TABLET`
    font-size: 28px;
    margin: 0 0 40px 0;
  `};
  }

  h2 {
    border-bottom: 1px solid ${({ theme }) => theme.colors.black};
    font-size: 22px;
    font-weight: 600;
    letter-spacing: 0.23px;
    line-height: 1.4;
    margin-bottom: 0;
    margin-top: 40px;

    &:target {
      border-bottom: 1px solid ${({ theme }) => theme.colors.blue};
      color: ${({ theme }) => theme.colors.blue};
    }
  }

  h3 {
    font-size: 17px;
    font-weight: 600;
    letter-spacing: 0.23px;
    line-height: 1.4;
    margin-bottom: 0;
    margin-top: 40px;
  }

  p {
    font-size: 14px;
    letter-spacing: 0.19px;
    line-height: 1.7;
    margin-bottom: 0;
    margin-top: 10px;
  }

  a {
    color: ${props => props.theme.colors.black};
    text-decoration: underline;
  }
`;

const FAQ: React.SFC<{}> = () => (
  <AppLayout>
    <Helmet>
      <title>Say - FAQ</title>
    </Helmet>

    <MainContent className="active">
      <h1>Frequently Asked Questions</h1>

      <Container>
        <h3 id="what-is-say">What Is Say?</h3>

        <p>
          Say creates meaningful connections between shareholders and their
          investments by partnering with the world’s most innovative companies
          and financial institutions.
        </p>

        <p>
          Say powers proxy voting for shareholders who invest at our broker
          partners and is creating new products for all shareholders to engage
          with companies, regardless of where they invest.
        </p>

        <h2 id="earnings-calls">Earnings calls</h2>

        <h3>Can I Use Say to Submit Questions for an Earnings Call?</h3>

        <p>
          Yes. Verified shareholders can submit questions to be asked on the
          call as well as vote for fellow shareholders’ submissions. The votes
          will be tallied and archived publicly on saytechnologies.com as well
          as delivered to the company’s investor relations department. It’s up
          to the company, however, to decide which ones they will answer on the
          call, if any.
        </p>

        <h3>Who Is a Verified Shareholder?</h3>

        <p>
          On Say, a verified shareholder is someone who has linked their
          brokerage account and confirmed that they own stock in the relevant
          company.
        </p>

        <h3>
          Does the Company Pick the Questions Based on How Many Votes They
          Receive?
        </h3>
        <p>
          Companies choose which questions they would like to answer. Though Say
          displays the number of votes each question received and how many
          shares those votes represent, companies are not obligated to consider
          these metrics.
        </p>

        <h3>
          Do I Have to Own Shares in the Company to Submit an Earnings Call
          Question?
        </h3>

        <p>
          Yes. Earnings calls are used by companies to go over financial results
          from the previous quarter and release other information to the
          investment community. Generally, companies have only allowed analysts,
          large stakeholders, and institutional investors to ask questions,
          while retail investors can listen in.
        </p>

        <h3>How Many Earnings Call Questions Can I Submit?</h3>
        <p>
          You can submit up to three questions per earnings call, and vote on an
          unlimited number of questions.
        </p>

        <h3>I Submitted an Earnings Call Question But It’s Not Posted. Why?</h3>
        <p>
          Our goal is to publish as many questions as possible from the
          company’s investors, but we won’t publish questions that are
          irrelevant or harmful. This includes questions that contain hate
          speech, obscenity, libel, factually incorrect information,
          confidential or non-public information, or any other harmful language.
        </p>

        <h2 id="connecting-a-brokerage">Connecting a Brokerage</h2>

        <h3>Do I Have to Connect my Brokerage Account?</h3>

        <p>
          In order to be a verified shareholder, you must link your brokerage
          account and confirm which companies you own stock in. Verified
          shareholders can access the full power of Say by engaging directly
          with the companies they own.
        </p>

        <h3>Do I Need to Invest Through a Specific Broker to Use Say?</h3>

        <p>
          You do not need to use a specific broker to verify your share
          ownership on Say. If you don’t see yours listed, send a message to{" "}
          <a href="mailto:support@saytechnologies.com">
            support@saytechnologies.com
          </a>{" "}
          with the name of your broker so we can help.
        </p>

        <h2 id="account-privacy">Account Privacy</h2>

        <h3>What Do You Do with My Information and How Do You Keep It Safe?</h3>

        <p>
          Please review our <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/terms">Terms of Service</Link> to see how we may use your
          information and what we do to keep it safe.
        </p>

        <p>
          Say follows industry-standard practices regarding protecting your
          information. Our secure data centers are located in the United States
          and all personally identifiable information stored by Say is
          encrypted.
        </p>

        <h3>How Do I Delete My Account?</h3>

        <p>
          Please email the Say support team at{" "}
          <a href="mailto:support@saytechnologies.com">
            support@saytechnologies.com
          </a>
          .
        </p>

        <h2 id="proxy-voting">Proxy Voting</h2>

        <h3>What Is a Proxy Vote?</h3>

        <p>
          Most shareholders have the right to regularly vote on certain issues
          related to the companies they are invested in in annual and special
          meetings of shareholders. Since not everyone attends such meetings in
          person--instead, they vote by proxy to make sure their votes are
          counted at the meeting.
        </p>

        <h3>Can I Use the Say App to Cast Proxy Votes?</h3>

        <p>
          No. Please contact your broker-dealer or investment adviser regarding
          how you may vote proxies for your investments.
        </p>

        <h2 id="say-communications">Say Communications</h2>

        <h3>Why Do I Receive Emails from Say About My Investments?</h3>

        <p>
          As a shareholder, you are required by law to receive certain
          information about your investments, including proxy statements and
          periodic reports. Say delivers these materials to shareholders who
          hold investments at our broker partners.
        </p>

        <p>
          Say is not a broker-dealer, investment adviser, tax adviser, or legal
          adviser.
        </p>

        <h3>Why Do Some Emails Not Allow Me to Vote?</h3>

        <p>
          Some communications are simply information for you to receive as a
          shareholder, such as annual and semi-annual reports from mutual funds
          and ETFs that you may own. This information will help you stay
          up-to-date with your investments, but there is no voting action for
          shareholders associated with it.
        </p>

        <h3>Have More Questions?</h3>

        <p>
          General inquiries:{" "}
          <a href="mailto:support@saytechnologies.com">
            support@saytechnologies.com
          </a>
        </p>
        <p>
          Press inquiries:{" "}
          <a href="mailto:press@saytechnologies.com">
            press@saytechnologies.com
          </a>
        </p>
      </Container>
    </MainContent>
    <Footer />
  </AppLayout>
);

export default FAQ;



// WEBPACK FOOTER //
// ./src/pages/FAQ/FAQ.tsx